import { useState, useEffect, createContext, useCallback, useRef } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import axios from './lib/apiTransport';
import { createRefreshCoordinator, REFRESH_TIMEOUT_MS } from './lib/authRefresh';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import GameDetail from './pages/GameDetail';
import CreateGame from './pages/CreateGame';
import Profile from './pages/Profile';
import CompleteDateOfBirth from './pages/CompleteDateOfBirth';
import AdminPanel from './pages/AdminPanel';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminNotifications from './pages/AdminNotifications';
import NotificationAuditLog from './pages/admin/NotificationAuditLog';
import NotificationTemplatesPage from './pages/admin/NotificationTemplates';
import NotificationPerformance from './pages/admin/NotificationPerformance';
import GameTimeline from './pages/admin/GameTimeline';
import GameTapeSearch from './pages/admin/GameTapeSearch';
import AdminUserControls from './pages/admin/AdminUserControls';
import AdminHostView from './pages/admin/AdminHostView';
import AdminHostClubView from './pages/admin/AdminHostClubView';
import AdminHostList from './pages/admin/AdminHostList';
import NavigationProgress from './components/NavigationProgress';
import IosEdgeSwipeBack from './components/IosEdgeSwipeBack';
import MobileNavigation from './components/MobileNavigation';
import GameHistory from './components/GameHistory';
import SMSMonitoring from './pages/SMSMonitoring';
import Rankings from './pages/Rankings';
import ResetPassword from './pages/ResetPassword';
import Notifications from './pages/Notifications';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import NotificationTransparency from './pages/NotificationTransparency';
import Following from './pages/Following';
import Followers from './pages/Followers';
import BlockedUsers from './pages/BlockedUsers';
import PublicProfile from './pages/PublicProfile';
import TrainerPage from './pages/TrainerPage';
import TrainerConnections from './pages/TrainerConnections';
import TrainerPrograms from './pages/TrainerPrograms';
import UserFollowers from './pages/UserFollowers';
import UserFollowing from './pages/UserFollowing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import About from './pages/About';
import CommunityGuidelines from './pages/CommunityGuidelines';
import Support from './pages/Support';
import HostDashboard from './pages/HostDashboard';
import HostReports from './pages/HostReports';
import PlayerHistory from './pages/PlayerHistory';
import ConfirmSpot from './pages/ConfirmSpot';
import JoinGame from './pages/JoinGame';
import JoinRedirect from './pages/JoinRedirect';
import RunPreview from './pages/RunPreview';
import SmsJoin from './pages/SmsJoin';
import AccountSettings from './pages/AccountSettings';
import VerifyEmailChange from './pages/VerifyEmailChange';
import PlayerSearch from './pages/PlayerSearch';
import ClubDetail from './pages/ClubDetail';
import CreateClub from './pages/CreateClub';
import MyClubs from './pages/MyClubs';
import { Toaster } from '@/components/ui/sonner';
import { logAppLoad } from './utils/engagementTracker';
import ErrorBoundary from './components/ErrorBoundary';
import { UniversalLinksHandler } from './lib/universalLinks';
import PushPermissionSheet from './components/PushPermissionSheet';
import {
  recoverPushRegistrationSilently,
  bindPushRecoveryLifecycle,
  abandonPushRegistration,
  unregisterOnLogout,
  bindNotificationTapHandler,
} from './lib/pushNotifications';

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const BACKEND_URL = RAW_BACKEND_URL.replace(/\/+$/, ''); // trim trailing slashes
export const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);

// ProfileRedirect: Handles legacy /profile/{user_id} links from old notifications
// Attempts to fetch user by ID and redirect to /player/{username}
const ProfileRedirect = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  
  useEffect(() => {
    const fetchAndRedirect = async () => {
      try {
        // Try to get user info by ID
        const res = await axios.get(`${API}/users/${userId}/public`);
        if (res.data?.username) {
          navigate(`/player/${res.data.username}`, { replace: true });
          return;
        }
      } catch (err) {
        console.error('[ProfileRedirect] Failed to fetch user:', err);
        console.error('[ProfileRedirect] Failed to fetch user:', err);
      }
      setError(true);
    };
    
    if (userId) {
      fetchAndRedirect();
    } else {
      navigate('/profile', { replace: true });
    }
  }, [userId, navigate]);
  
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-zinc-900 mb-2">Profile Unavailable</h2>
          <p className="text-zinc-600 mb-4">This profile could not be found.</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
        <p className="text-zinc-600">Loading profile...</p>
      </div>
    </div>
  );
};

// ============================================================================
// AUTH LOGGING - Detailed logging for debugging mobile session issues
// ============================================================================
const AUTH_LOG_PREFIX = '[AUTH]';

const authLog = (message, data = null) => {
  const timestamp = new Date().toISOString();
  if (data) {
  } else {
  }
};

const authWarn = (message, data = null) => {
  const timestamp = new Date().toISOString();
  if (data) {
  } else {
  }
};

const authError = (message, data = null) => {
  const timestamp = new Date().toISOString();
  if (data) {
    console.error(`${AUTH_LOG_PREFIX} [${timestamp}] ❌ ${message}`, data);
    console.error(`${AUTH_LOG_PREFIX} [${timestamp}] ❌ ${message}`, data);
  } else {
    console.error(`${AUTH_LOG_PREFIX} [${timestamp}] ❌ ${message}`);
    console.error(`${AUTH_LOG_PREFIX} [${timestamp}] ❌ ${message}`);
  }
};

// ============================================================================
// TOKEN UTILITIES
// ============================================================================

// Helper to get current access token (always fresh from localStorage)
export const getAccessToken = () => {
  try {
    return localStorage.getItem('access_token');
  } catch (e) {
    authError('Failed to read access_token from localStorage', e.message);
    return null;
  }
};

// Helper to get refresh token
const getRefreshToken = () => {
  try {
    return localStorage.getItem('refresh_token');
  } catch (e) {
    authError('Failed to read refresh_token from localStorage', e.message);
    return null;
  }
};

// Safe localStorage write with verification
const safeSetToken = (key, value) => {
  try {
    localStorage.setItem(key, value);
    // Verify the write succeeded
    const verified = localStorage.getItem(key);
    if (verified !== value) {
      authError(`Token storage verification FAILED for ${key}`);
      return false;
    }
    return true;
  } catch (e) {
    authError(`Failed to write ${key} to localStorage`, e.message);
    return false;
  }
};

// Decode a base64url string (JWT segments are base64url, not standard base64,
// and may omit '=' padding). Normalize, pad, then UTF-8-safe decode.
const base64UrlDecode = (segment) => {
  let b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad) b64 += '='.repeat(4 - pad);
  const binary = atob(b64);
  try {
    // UTF-8 safe: turn raw bytes back into a proper Unicode string
    return decodeURIComponent(
      binary
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch {
    // Fallback for plain-ASCII payloads if percent-decoding fails
    return binary;
  }
};

// Helper to decode JWT payload
const decodeToken = (token) => {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return null;
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    return payload;
  } catch (e) {
    authWarn('Failed to decode token', e.message);
    return null;
  }
};

// Get token expiry info
const getTokenExpiry = (token) => {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return null;
  
  const expMs = payload.exp * 1000;
  const nowMs = Date.now();
  const remainingMs = expMs - nowMs;
  const remainingMin = Math.round(remainingMs / 60000);
  
  return {
    expiresAt: new Date(expMs).toISOString(),
    remainingMs,
    remainingMin,
    isExpired: remainingMs <= 0,
    isExpiringSoon: remainingMs < 5 * 60 * 1000, // < 5 minutes
  };
};

// Check if token is expired or expiring soon
const isTokenExpiredOrExpiringSoon = (token) => {
  if (!token) return { expired: true, expiringSoon: true, reason: 'no_token' };
  
  const expiry = getTokenExpiry(token);
  if (!expiry) return { expired: true, expiringSoon: true, reason: 'invalid_token' };
  
  return {
    expired: expiry.isExpired,
    expiringSoon: expiry.isExpiringSoon,
    remainingMin: expiry.remainingMin,
    reason: expiry.isExpired ? 'expired' : (expiry.isExpiringSoon ? 'expiring_soon' : 'valid'),
  };
};

// ============================================================================
// AXIOS CLIENT SETUP
// ============================================================================

export const apiClient = axios.create({
  baseURL: API,
  headers: { 'X-OpenRuns-Client-Protocol': '2' },
});

// Simple guard so we don't refresh for auth routes
const isAuthRoute = (url = '') => url.includes('/auth/login') || url.includes('/auth/refresh');

// Track refresh state to prevent race conditions
let authGeneration = 0;
let refreshUnresolved = false;
// Lifecycle and HTTP callers share one generation-scoped refresh cycle.
const markAuthUnresolved = result => {
  refreshUnresolved = true;
  delete apiClient.defaults.headers.common.Authorization;
  window.dispatchEvent(new CustomEvent('openruns:auth-unresolved', { detail: result || { pending: true } }));
};
const refreshCoordinator = createRefreshCoordinator({
  request: (refreshToken, { timeout, signal }) => axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken }, { timeout, signal }),
  storage: {
    getItem: key => localStorage.getItem(key),
    setItem: (key, value) => localStorage.setItem(key, value),
    removeItem: key => localStorage.removeItem(key),
  },
  generation: () => authGeneration,
  validateAccess: access => {
    const expiry = getTokenExpiry(access);
    return expiry !== null && Number.isFinite(expiry.remainingMs) && expiry.remainingMs > 0;
  },
  onUnresolved: markAuthUnresolved,
  onRejected: result => forceLogout(result.reason),
  onStorageFailure: result => {
    invalidateAuthContext();
    refreshUnresolved = true;
    delete apiClient.defaults.headers.common.Authorization;
    window.dispatchEvent(new CustomEvent('openruns:auth-storage-failed', { detail: result }));
  },
  onSuccess: result => {
    refreshUnresolved = false;
    apiClient.defaults.headers.common.Authorization = `Bearer ${result.accessToken}`;
    window.dispatchEvent(new Event('openruns:auth-refreshed'));
  },
});
export const performTokenRefresh = (reason, options) => {
  const promise = refreshCoordinator.refresh(reason, options);
  promise.then(result => {
    if (result.kind === 'contract' || result.kind === 'storage') authError(`Authentication restoration failed: ${result.reason}`);
  });
  return promise;
};

// Force logout with reason logging
const invalidateAuthContext = () => {
  authGeneration += 1;
  refreshUnresolved = false;
  refreshCoordinator.invalidate();
  abandonPushRegistration();
};

const forceLogout = (reason, detail = null) => {
  invalidateAuthContext();
  abandonPushRegistration();
  authError(`FORCING LOGOUT: ${reason}`, detail);
  
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  } catch (e) {
    authError('Failed to clear tokens from localStorage', e.message);
  }
  
  delete apiClient.defaults.headers.common.Authorization;
  window.dispatchEvent(new Event('openruns:auth-rejected'));
  
  // Redirect to login
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

// ============================================================================
// AXIOS INTERCEPTORS
// ============================================================================

// Request interceptor - check token before each request
apiClient.interceptors.request.use(async (config) => {
  const started = authGeneration;
  config.authGeneration = started;
  // Skip auth routes
  if (isAuthRoute(config.url)) {
    return config;
  }
  if (refreshCoordinator.recoveryState().fatal) throw new axios.CanceledError('Credential storage requires a new sign in');
  
  let token = getAccessToken();
  const tokenStatus = isTokenExpiredOrExpiringSoon(token);
  
  // If token is expired or expiring soon, refresh first
  if ((token || getRefreshToken()) && (tokenStatus.expired || tokenStatus.expiringSoon || refreshUnresolved)) {
    authLog(`Token ${tokenStatus.reason} (${tokenStatus.remainingMin}min remaining) - refreshing before request`);
    
    const result = await performTokenRefresh(tokenStatus.reason);
    
    if (started !== authGeneration) throw new axios.CanceledError('Abandoned account');
    if (result.success) {
      token = result.accessToken;
    } else {
      throw new axios.CanceledError(`Authentication restoration unresolved: ${result.kind}`);
    }
  }
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  return config;
});

// Response interceptor - handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    if (originalRequest.authGeneration !== undefined && originalRequest.authGeneration !== authGeneration) return Promise.reject(error);
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    
    // Only handle 401 for non-auth routes that haven't been retried
    if (status === 401 && !originalRequest._retry && !isAuthRoute(originalRequest.url)) {
      originalRequest._retry = true;

      // An older request may fail after another caller already rotated tokens.
      const currentAccess = getAccessToken();
      const sentAccess = originalRequest.headers?.Authorization ?? originalRequest.headers?.get?.('Authorization');
      if (!refreshUnresolved && currentAccess && sentAccess !== `Bearer ${currentAccess}` && !isTokenExpiredOrExpiringSoon(currentAccess).expiringSoon) {
        originalRequest.headers.Authorization = `Bearer ${currentAccess}`;
        return apiClient(originalRequest);
      }
      
      authLog('Received 401, attempting token refresh');
      
      const result = await performTokenRefresh('401_response');
      
      if (originalRequest.authGeneration !== authGeneration) return Promise.reject(error);
      if (result.success) {
        
        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
        return apiClient(originalRequest);
      } else {
        // Only the coordinator's definitive refresh rejection clears storage.
        markAuthUnresolved({ ...result, pending: false });
        return Promise.reject(error);
      }
    }
    
    // Handle 403 - ONLY logout for account-level blocks (disabled/suspended)
    // Do NOT logout for game-specific 403s (age restrictions, block rules, etc.)
    if (status === 403) {
      const detailLower = (detail || '').toLowerCase();
      const isAccountLevel = detailLower.includes('account') && 
        (detailLower.includes('disabled') || detailLower.includes('suspended'));
      
      if (isAccountLevel) {
        authError('Received 403 - account disabled/suspended', detail);
        markAuthUnresolved();
        await performTokenRefresh('account_forbidden');
      } else {
        // Game-specific 403s (age, blocks, permissions) should NOT cause logout
        authWarn('Received 403 - access denied (not account-level)', detail);
      }
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

// ============================================================================
// APP COMPONENT
// ============================================================================

// PushNotificationTapBinder: hooks the Capacitor push tap listener to React
// Router. Lives inside BrowserRouter so it can call useNavigate(). Web no-op.
const PushNotificationTapBinder = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const unbind = bindNotificationTapHandler(navigate);
    return () => { try { unbind?.(); } catch { /* noop */ } };
  }, [navigate]);
  return null;
};

export const STARTUP_LOADING_DELAY_MS = 650;

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getAccessToken());
  const [loading, setLoading] = useState(true);
  const [recovery, setRecovery] = useState(null);
  const [showStartupLoading, setShowStartupLoading] = useState(false);
  const manualSignIn = useRef(false);
  const validationRequest = useRef(null);
  // Club link return flow: when an anonymous user taps "Join Club" on
  // /club/:slug, ClubDetail.jsx calls setShowAuth(true) and stores the slug
  // via setPendingClubSlug(slug). These hooks live at root so any route can
  // open the auth UI without crashing the React tree.
  const [showAuth, setShowAuthState] = useState(false);
  const [pendingClubSlug, setPendingClubSlugState] = useState(() => {
    try { return localStorage.getItem('pendingClubSlug') || null; } catch { return null; }
  });
  const lastVisibilityCheck = useRef(Date.now());
  const bootstrapInProgress = useRef(false);

  // setShowAuth: when toggled true from a non-Landing route, hard-redirect to
  // "/?openAuth=1" so Landing mounts and opens the auth modal/sheet. Hard
  // navigation is used (instead of React Router) because BrowserRouter lives
  // inside the AuthContext provider and we cannot call useNavigate here.
  const setShowAuth = useCallback((value) => {
    setShowAuthState(value);
    if (value && typeof window !== 'undefined' && window.location.pathname !== '/') {
      window.location.assign('/?openAuth=1');
    }
  }, []);

  // setPendingClubSlug: mirror into localStorage so Landing.jsx can return the
  // user to the club page after auth. It must not create membership by itself.
  const setPendingClubSlug = useCallback((slug) => {
    setPendingClubSlugState(slug);
    try {
      if (slug) {
        localStorage.setItem('pendingClubSlug', slug);
      } else {
        localStorage.removeItem('pendingClubSlug');
      }
    } catch (e) {
      authWarn('Failed to persist pendingClubSlug', e.message);
    }
  }, []);

  // Bootstrap auth - called on initial load and on resume from background
  const bootstrapAuth = useCallback(async (reason = 'initial') => {
    if (manualSignIn.current) return;
    if (refreshCoordinator.recoveryState().fatal) {
      setUser(null); setToken(null); setLoading(false);
      setRecovery(refreshCoordinator.recoveryState().failure);
      return;
    }
    // Prevent concurrent bootstrap calls
    if (bootstrapInProgress.current) {
      if (reason === 'manual_retry') performTokenRefresh('manual_retry', { userInitiated: true });
      authLog('Bootstrap already in progress, skipping');
      return;
    }
    
    const started = authGeneration;
    bootstrapInProgress.current = true;
    setUser(null);
    setToken(null);
    setRecovery(previous => previous?.kind ? previous : null);
    setLoading(true);
    authLog(`Bootstrap auth starting (reason: ${reason})`);
    
    try {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();
      
      if (!accessToken && !refreshToken) {
        authLog('No tokens found - user not logged in');
        setToken(null);
        setUser(null);
        setLoading(false);
        setRecovery(null);
        return;
      }
      
      // Check token status
      const tokenStatus = isTokenExpiredOrExpiringSoon(accessToken);
      authLog(`Token status: ${tokenStatus.reason} (${tokenStatus.remainingMin}min remaining)`);
      
      // If token is expired or expiring soon, refresh first
      if (tokenStatus.expired || tokenStatus.expiringSoon || refreshUnresolved) {
        authLog('Token needs refresh before bootstrap');
        
        if (!refreshToken) {
          authWarn('No refresh token available - forcing logout');
          setToken(null);
          setUser(null);
          setLoading(false);
          setRecovery({ kind: 'contract', pending: false });
          return;
        }
        
        const result = await performTokenRefresh(`bootstrap_${tokenStatus.reason}`, { userInitiated: reason === 'manual_retry' });
        if (started !== authGeneration) return;
        
        if (!result.success) {
          authWarn(`Bootstrap restoration unresolved: ${result.kind}`);
          setToken(null);
          setUser(null);
          setLoading(false);
          setRecovery({ ...result, pending: false });
          return;
        }
        
        // Update local state with new token
        setToken(result.accessToken);
        setRecovery(null);
      }
      
      // Fetch user data
      authLog('Fetching user data...');
      const controller = new AbortController();
      validationRequest.current = controller;
      const { data } = await apiClient.get('/auth/me', { timeout: REFRESH_TIMEOUT_MS, signal: controller.signal });
      if (started !== authGeneration) return;
      
      setUser(data);
      setToken(getAccessToken()); // Get fresh token after potential refresh
      setRecovery(null);
      
      authLog('Bootstrap complete - user authenticated', { username: data.username });

      // Capture the recipient's IANA timezone (used as the quiet-hours clock for
      // SMS/push). Fire-and-forget; only writes when it differs from the stored
      // value. Never affects auth/session state.
      try {
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (browserTz && browserTz !== data.tz) {
          apiClient.post('/users/timezone', { timezone: browserTz }).catch(() => {});
        }
      } catch { /* noop */ }
      
      // Re-register the iOS push token on every authenticated cold launch. A
      // reinstall issues a NEW APNs token; previously only explicit login()
      // re-registered, so a restored-session launch kept the backend pointed at
      // stale tokens (silent non-delivery). Idempotent / no-op on web.
      recoverPushRegistrationSilently().catch(() => {});
      
      // Log app load event — fire-and-forget; analytics/logging failures must
      // never affect auth/session state (sync throw or rejected promise alike).
      try { Promise.resolve(logAppLoad()).catch(() => {}); } catch { /* noop */ }
      
    } catch (err) {
      if (started !== authGeneration) return;
      // Protected UI needs a validated user; only refresh rejection clears storage.
      authWarn('Bootstrap user validation unresolved', { status: err.response?.status || 'no_response' });
      setToken(null);
      setUser(null);
      setRecovery({ kind: 'transient', pending: false });
    } finally {
      bootstrapInProgress.current = false;
      if (started === authGeneration) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const unresolved = event => {
      if (manualSignIn.current) return;
      const detail = { ...event.detail, pending: Boolean(event.detail?.pending) };
      setUser(null); setToken(null); setLoading(detail.pending);
      setRecovery(previous => detail.kind ? detail : previous?.kind ? { ...previous, ...detail } : null);
    };
    const rejected = () => { setUser(null); setToken(null); setLoading(false); setRecovery(null); };
    const storageFailed = event => { setUser(null); setToken(null); setLoading(false); setRecovery(event.detail); };
    const refreshed = () => { setRecovery(null); bootstrapAuth('refresh_success'); };
    window.addEventListener('openruns:auth-unresolved', unresolved);
    window.addEventListener('openruns:auth-rejected', rejected);
    window.addEventListener('openruns:auth-storage-failed', storageFailed);
    window.addEventListener('openruns:auth-refreshed', refreshed);
    return () => {
      window.removeEventListener('openruns:auth-unresolved', unresolved);
      window.removeEventListener('openruns:auth-rejected', rejected);
      window.removeEventListener('openruns:auth-storage-failed', storageFailed);
      window.removeEventListener('openruns:auth-refreshed', refreshed);
    };
  }, [bootstrapAuth]);

  useEffect(() => {
    setShowStartupLoading(false);
    if (!loading || recovery) return;
    const started = authGeneration;
    const timer = setTimeout(() => {
      if (started === authGeneration) setShowStartupLoading(true);
    }, STARTUP_LOADING_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loading, recovery]);

  useEffect(() => {
    if (!recovery?.retryAt || recovery.retryAt <= Date.now()) return;
    const timer = setTimeout(() => setRecovery(value => value ? { ...value } : value), recovery.retryAt - Date.now());
    return () => clearTimeout(timer);
  }, [recovery?.retryAt]);

  // Initial bootstrap on mount
  useEffect(() => {
    bootstrapAuth('initial_mount');
  }, [bootstrapAuth]);

  useEffect(() => bindPushRecoveryLifecycle(), []);

  // Handle visibility change (app resume from background)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const timeSinceLastCheck = now - lastVisibilityCheck.current;
        const timeSinceLastCheckMin = Math.round(timeSinceLastCheck / 60000);
        
        authLog(`App became visible (was hidden for ${timeSinceLastCheckMin} minutes)`);
        lastVisibilityCheck.current = now;
        
        // Only re-bootstrap if we were hidden for more than 1 minute
        // This prevents unnecessary refreshes on quick tab switches
        if (timeSinceLastCheck > 60000) {
          const accessToken = getAccessToken();
          const tokenStatus = isTokenExpiredOrExpiringSoon(accessToken);
          
          authLog(`Resume check - token status: ${tokenStatus.reason}`);
          
          // If token is expired or expiring soon, re-bootstrap to refresh
          if (tokenStatus.expired || tokenStatus.expiringSoon || !user) {
            authLog('Token needs refresh after resume - re-bootstrapping');
            bootstrapAuth('visibility_resume');
          } else {
            authLog('Token still valid after resume');
          }
        }
      } else {
        authLog('App became hidden');
        lastVisibilityCheck.current = Date.now();
      }
    };

    // Handle page focus (additional signal for some browsers)
    const handleFocus = () => {
      authLog('Window focused');
      // Trigger visibility check logic
      if (document.visibilityState === 'visible') {
        const accessToken = getAccessToken();
        const tokenStatus = isTokenExpiredOrExpiringSoon(accessToken);
        
        if (tokenStatus.expired || tokenStatus.expiringSoon || !user) {
          authLog('Token needs refresh after focus - re-bootstrapping');
          bootstrapAuth('window_focus');
        }
      }
    };

    // Handle online/offline
    const handleOnline = () => {
      authLog('Network came online');
      const accessToken = getAccessToken();
      const tokenStatus = isTokenExpiredOrExpiringSoon(accessToken);
      
      if (tokenStatus.expired || tokenStatus.expiringSoon || !user) {
        authLog('Token needs refresh after coming online - re-bootstrapping');
        bootstrapAuth('network_online');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, [bootstrapAuth, user]);

  // User-only work shares the existing authentication generation. Refresh may
  // rotate credentials within this generation; login/logout invalidate it.
  const beginUserUpdate = () => {
    const started = authGeneration;
    return {
      isCurrent: () => started === authGeneration,
      apply: userData => {
        if (started !== authGeneration) return false;
        setUser(current => started === authGeneration
          ? (typeof userData === 'function' ? userData(current) : userData) : current);
        return true;
      },
    };
  };

  // Login function
  const login = (accessToken, refreshToken, userData) => {
    validationRequest.current?.abort();
    invalidateAuthContext();
    abandonPushRegistration();
    authLog('Login called', { username: userData?.username });
    
    const accessStored = safeSetToken('access_token', accessToken);
    const refreshStored = safeSetToken('refresh_token', refreshToken);
    
    if (!accessStored || !refreshStored) {
      authError('Failed to store tokens during login');
      setUser(null); setToken(null); setLoading(false);
      manualSignIn.current = false;
      setRecovery({ kind: 'storage', reason: 'credential_storage_unrecoverable', credentialsCleared: false });
      return;
    }
    refreshCoordinator.resetAfterLogin();
    manualSignIn.current = false;
    setRecovery(null);
    setLoading(false);
    
    apiClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(userData);
    
    const expiry = getTokenExpiry(accessToken);
    authLog(`Login complete - token expires in ${expiry?.remainingMin} minutes`);
    
    // If user previously granted iOS push permission, silently re-register
    // the device token under this user (so device handoff works on shared
    // devices). No-op on web.
    recoverPushRegistrationSilently().catch(() => {});
  };

  // Logout function
  const logout = async () => {
    validationRequest.current?.abort();
    const departingAccess = getAccessToken();
    invalidateAuthContext();
    const departed = authGeneration;
    const stillDeparted = () => authGeneration === departed;
    const settle = async (task) => {
      let timer;
      try {
        return await Promise.race([
          Promise.resolve().then(task).then(Boolean, () => false),
          new Promise(resolve => { timer = setTimeout(() => resolve(false), 5000); }),
        ]);
      } finally { clearTimeout(timer); }
    };
    const pushCleanup = settle(() => unregisterOnLogout({ access: departingAccess, isCurrent: stillDeparted }));
    const sessionCleanup = settle(async () => {
      if (!departingAccess) return false;
      // Bypass refresh interceptors; never borrow a later account's credentials.
      const response = await fetch(`${API}/auth/logout`, {
        method: 'POST', headers: { Authorization: `Bearer ${departingAccess}`, 'X-OpenRuns-Client-Protocol': '2' },
      });
      return response.ok;
    });
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } catch (e) { authError('Failed to clear localStorage', e.message); }
    delete apiClient.defaults.headers.common.Authorization;
    setToken(null);
    setUser(null);
    setLoading(false);
    setRecovery(null);
    const [pushConfirmed, sessionConfirmed] = await Promise.all([pushCleanup, sessionCleanup]);
    if (!pushConfirmed) authWarn('Push logout cleanup unconfirmed');
    if (!sessionConfirmed) authWarn('Remote session revocation unconfirmed');
    return { pushConfirmed, sessionConfirmed };
  };

  const beginSignIn = () => {
    manualSignIn.current = true;
    validationRequest.current?.abort();
    invalidateAuthContext();
    setUser(null); setToken(null); setLoading(false); setRecovery(null);
    // Open the existing Landing authentication flow without reloading credentials.
    window.history.replaceState(window.history.state, '', '/?openAuth=1');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  if (recovery && !manualSignIn.current) {
    const storageFailed = recovery.reason === 'credential_storage_unrecoverable';
    return (
      <main className="min-h-screen flex items-center justify-center bg-orange-50 p-6">
        <section className="w-full max-w-sm rounded-xl bg-white p-6 shadow-sm" aria-live="polite">
          <p className="text-lg font-semibold">{storageFailed ? 'Couldn’t save your session. Please sign in again.' : recovery.pending && !recovery.kind ? 'Restoring your session…' : 'Couldn’t restore your session.'}</p>
          <div className="mt-5 flex gap-4">
            {!storageFailed && <button type="button" className="rounded-lg border px-4 py-2" disabled={recovery.pending && recovery.phase !== 'waiting'} onClick={() => bootstrapAuth('manual_retry')}>{recovery.pending && recovery.phase !== 'waiting' ? recovery.phase === 'request' && recovery.attempt >= 2 ? 'Retrying...' : 'Restoring...' : 'Retry'}</button>}
            <button type="button" className="rounded-lg bg-orange-600 px-4 py-2 text-white" onClick={beginSignIn}>Sign In</button>
          </div>
          {recovery.rateLimitUntil > Date.now() && <p className="mt-3 text-sm">The server asked us to wait. Retry can proceed after {new Date(recovery.rateLimitUntil).toLocaleTimeString()}.</p>}
        </section>
      </main>
    );
  }

  if (loading) {
    return (
      <div aria-busy="true" aria-label="Opening Open Runs" className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
        {showStartupLoading && <div className="text-xl font-semibold text-orange-600">Loading...</div>}
      </div>
    );
  }

  // Protected self/account routes require a VALIDATED user, not just a token.
  // A token-without-user state (e.g. transient /auth/me failure that preserved
  // the token) must NOT render protected UI — route to Landing/Login instead.
  // Auth-loading is already handled by the spinner above.
  const requireUser = (element) => (user ? element : <Navigate to="/" replace />);
  const requireStrictAdmin = (element) => (user?.role === 'admin'
    ? element
    : <Navigate to={user ? '/dashboard' : '/'} replace />);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, beginUserUpdate, showAuth, setShowAuth, pendingClubSlug, setPendingClubSlug }}>
      <div className="App">
        <BrowserRouter>
          <NavigationProgress />
          <IosEdgeSwipeBack />
          <ErrorBoundary>
          <UniversalLinksHandler />
          <PushNotificationTapBinder />
            <MobileNavigation><Routes>
            <Route path="/" element={(token && user) ? <Navigate to="/dashboard" /> : <Landing />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/about" element={<About />} />
            <Route path="/trainers" element={<TrainerPrograms />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/support" element={<Support />} />
            <Route path="/confirm" element={<ConfirmSpot />} />
            <Route path="/join/:gameId" element={<JoinGame />} />
            <Route path="/j/:code" element={<JoinRedirect />} />
            <Route path="/r/:runId" element={<SmsJoin />} />
            <Route path="/run/:runId" element={<RunPreview />} />
            {/* Run Club Routes (Phase 10A) */}
            <Route path="/club/:slug" element={<ClubDetail />} />
            <Route path="/create-club" element={requireUser(<CreateClub />)} />
            <Route path="/my-clubs" element={requireUser(<MyClubs />)} />
            <Route path="/dashboard" element={requireUser(<Dashboard />)} />
            <Route path="/games/:id" element={requireUser(<GameDetail />)} />
            <Route path="/create-game" element={requireUser(<CreateGame />)} />
            <Route path="/profile" element={requireUser(<Profile />)} />
            <Route path="/complete-dob" element={requireUser(<CompleteDateOfBirth />)} />
            {/* Legacy profile link redirect - handles old /profile/{user_id} format */}
            <Route path="/profile/:userId" element={token ? <ProfileRedirect /> : <Navigate to="/" />} />
            <Route path="/account-settings" element={requireUser(<AccountSettings />)} />
            <Route path="/player-search" element={requireUser(<PlayerSearch />)} />
            <Route path="/verify-email-change" element={<VerifyEmailChange />} />
            <Route path="/player/:username" element={token ? <PublicProfile /> : <Navigate to="/" />} />
            <Route path="/my-trainer-page" element={requireUser(<TrainerPage ownerPreview />)} />
            <Route path="/host/:userId" element={<TrainerPage />} />
            <Route path="/host/:userId/followers" element={<TrainerConnections direction="followers" />} />
            <Route path="/host/:userId/following" element={<TrainerConnections direction="following" />} />
            <Route path="/player/:username/followers" element={token ? <UserFollowers /> : <Navigate to="/" />} />
            <Route path="/player/:username/following" element={token ? <UserFollowing /> : <Navigate to="/" />} />
            <Route path="/profile/following" element={requireUser(<Following />)} />
            <Route path="/profile/followers" element={requireUser(<Followers />)} />
            <Route path="/profile/blocked" element={requireUser(<BlockedUsers />)} />
            <Route path="/notifications" element={requireUser(<Notifications />)} />
            <Route path="/notification-settings" element={requireUser(<NotificationSettingsPage />)} />
            <Route path="/notification-settings/how-it-works" element={requireUser(<NotificationTransparency />)} />
            <Route path="/admin" element={requireUser(<AdminPanel />)} />
            <Route path="/admin/users/:userId/controls" element={requireUser(<AdminUserControls />)} />
            <Route path="/admin/hosts" element={requireUser(<AdminHostList />)} />
            <Route path="/admin/hosts/:userId" element={requireUser(<AdminHostView />)} />
            <Route path="/admin/hosts/:userId/clubs/:clubId" element={requireUser(<AdminHostClubView />)} />
            <Route path="/admin/analytics" element={requireUser(<AdminAnalytics />)} />
            <Route path="/admin/notifications" element={requireUser(<AdminNotifications />)} />
            <Route path="/admin/notification-audit" element={requireUser(<NotificationAuditLog />)} />
            <Route path="/admin/notification-templates" element={requireUser(<NotificationTemplatesPage />)} />
            <Route path="/admin/notification-performance" element={requireStrictAdmin(<NotificationPerformance />)} />
            <Route path="/admin/sms-monitoring" element={requireUser(<SMSMonitoring />)} />
            <Route path="/admin/game-tape" element={requireUser(<GameTapeSearch />)} />
            <Route path="/admin/games/:gameId/timeline" element={requireUser(<GameTimeline />)} />
            <Route path="/games/:gameId/history" element={requireUser(<GameHistory />)} />
            <Route path="/rankings" element={requireUser(<Rankings />)} />
            <Route path="/host" element={requireUser(<HostDashboard />)} />
            <Route path="/host/reports" element={requireUser(<HostReports />)} />
            <Route path="/my-history" element={requireUser(<PlayerHistory />)} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes></MobileNavigation>
          <PushPermissionSheet />
          </ErrorBoundary>
        </BrowserRouter>
        <Toaster />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
